import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';

// Context
import { AppContext } from 'Context/AppContext';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';
import RefreshOverlay from 'Components/DesignSystem/Table/RefreshOverlay';
import Button from 'Components/DesignSystem/Button/Button';
import Search from 'Components/DesignSystem/Search/Search';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import { ArrowDown, ArrowRight, Hashtag, Cross } from 'Components/DesignSystem/Iconography/Icons';

// API Services
import { getUnmappedProducts } from 'api/DataReview';

// Utilities
import { transformToUnmappedRow, transformUnmappedPagination, PaginationInfo } from '../../utils';

// Mapping page
import { MappingContextProvider } from 'Pages/GMS/Mapping/Contexts/MappingContext';
import MappingTabView from 'Pages/GMS/Mapping/Components/MappingTabView';
import { ArsContextProvider } from 'Pages/GMS/ARS/Contexts/ArsContext';

// Components
import ExpandedRowContent from '../../components/expanded-row/ExpandedRowContent';
import TablePaginationBar from '../../components/layout/TablePaginationBar';

// Types
import { UnmappedAttributeRow, APIUnmappedProduct, ProductAttribute } from '../../types';

// View props interface
interface MissingMappingViewProps {
  onRefresh?: () => void;
}

// Internal row state type
interface MissingMappingRowState {
  product: UnmappedAttributeRow;
  isSelected: boolean;
  isExpanded: boolean;
  rawProduct: APIUnmappedProduct;
}

// Default pagination values
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;

// Background refresh cadence. Long enough that the table stays current
// for a passively-watching SME without thrashing the API; short enough
// that an external mapping change shows up before the user thinks to
// reload.
const POLL_INTERVAL_MS = 30000;

/**
 * MissingMappingView Component
 *
 * Displays products with unmapped attributes and values using SINGLE UNIFIED TABLE
 * Columns: # | Product | Category | Unmapped Attribute | Unmapped Attribute Value | Actions | Expand
 *
 * Key features:
 * - Custom simplified action bar (right-aligned): Mapping checkbox + Filter button
 * - Search in the action bar is the DS `Search` (2026-09-09) — it was a hand-rolled
 *   <input> with a colour-swap focus and no clear affordance (the "NO Search per Figma"
 *   note this line used to carry predates the search's arrival)
 * - Grey badges for counts (grey-200 bg, grey-500 border, grey-700 text)
 * - 3-dot actions menu in each row
 * - Supports row expansion with attribute/value chips
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11680-17774
 */
const MissingMappingView: React.FC<MissingMappingViewProps> = ({ onRefresh }) => {
  // Get channelKey from AppContext
  const appContext = useContext(AppContext);
  const channelKey = appContext?.channelKey || '';

  // State
  const [unmappedRows, setUnmappedRows] = useState<MissingMappingRowState[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMappingDrawerOpen, setIsMappingDrawerOpen] = useState(false);
  // Raw input value (instant) vs the debounced value passed to the API so each
  // keystroke doesn't fire a request. 300ms is the conventional input debounce.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    total: 0,
    totalPages: 0,
  });

  // Debounce the search input -> committed searchTerm, and reset to page 1
  // whenever the term changes (so the user always sees the head of the
  // filtered result set rather than a stale page index).
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPagination((prev) =>
        prev.page === DEFAULT_PAGE ? prev : { ...prev, page: DEFAULT_PAGE }
      );
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // Tracks whether the component is still mounted. Guards setState calls
  // inside async fetchProducts so a fetch that resolves after tab-switch
  // doesn't fire a "state update on unmounted component" warning.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Fetch products from API.
   *
   * `silent` flag is used by the background poll to refresh data without
   * toggling the loading spinner or surfacing a toast on transient errors —
   * a polled fetch failure shouldn't interrupt the user.
   */
  const fetchProducts = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!channelKey) return;

    if (!silent) setIsLoading(true);
    try {
      const response = await getUnmappedProducts(channelKey, {
        page: pagination.page,
        limit: pagination.perPage,
        search: searchTerm || undefined,
      });

      if (!isMountedRef.current) return;

      if (response.done && response.product_data) {
        setUnmappedRows((prev) => {
          // Carry forward per-row UI state (expanded, selected) across
          // refetches so a background poll doesn't snap an expanded row
          // shut while the user is reading it.
          const prevById = new Map<string, MissingMappingRowState>(
            prev.map((r) => [String(r.product.id), r])
          );
          return response.product_data.map((product: APIUnmappedProduct) => {
            const transformed = transformToUnmappedRow(product);
            const carry = prevById.get(String(transformed.id));
            return {
              product: transformed,
              isSelected: carry?.isSelected ?? false,
              isExpanded: carry?.isExpanded ?? false,
              rawProduct: product,
            };
          });
        });
        setPagination(transformUnmappedPagination(response.page));
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Failed to fetch unmapped products:', error);
      if (!silent) {
        toast.error('Failed to load unmapped products');
        setUnmappedRows([]);
      }
    } finally {
      if (isMountedRef.current && !silent) setIsLoading(false);
    }
  }, [channelKey, pagination.page, pagination.perPage, searchTerm]);

  // Fetch products when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Background poll: refresh every POLL_INTERVAL_MS while the tab is
  // visible and the mapping drawer isn't open. Skip when hidden so we
  // don't burn requests on backgrounded tabs; pause while the drawer
  // is open since drawer-close already triggers a fresh fetch and a
  // mid-edit table swap is jarring.
  useEffect(() => {
    if (isMappingDrawerOpen) return;

    const tick = () => {
      if (document.visibilityState === 'visible') {
        fetchProducts({ silent: true });
      }
    };

    const interval = window.setInterval(tick, POLL_INTERVAL_MS);
    // Refresh immediately when the tab regains focus, so a user
    // returning after >30s of being away sees current data without
    // waiting for the next interval.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchProducts, isMappingDrawerOpen]);

  // Handlers

  const handleRowExpand = (index: number) => {
    setUnmappedRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isExpanded: !updated[index].isExpanded };
      return updated;
    });
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPerPage: string | number) => {
    setPagination((prev) => ({ ...prev, perPage: newPerPage as number, page: DEFAULT_PAGE }));
  };

  // Build imported attributes from raw API product for ExpandedRowContent
  const buildImportedAttributes = (rawProduct: APIUnmappedProduct): ProductAttribute[] => {
    // Primary: full attributes array from API (shows all attributes with actual values)
    if (rawProduct.attributes && rawProduct.attributes.length > 0) {
      return rawProduct.attributes.map((attr) => ({
        name: attr.attribute,
        value: attr.values.join(', '),
        values: attr.values,
        // AB#14859. This view reuses ExpandedRowContent, which already renders the custom tag
        // for the other Data Review tabs — it was simply never given the flag here, so the tag
        // silently never appeared on Missing Mapping.
        isCustom: attr.is_custom === true,
      }));
    }
    // Fallback: unmapped-only (when attributes not present in response)
    const attrs: ProductAttribute[] = [];
    (rawProduct.attributes_with_unmapped_values || []).forEach((attr) => {
      attrs.push({
        name: attr.attribute,
        value: attr.unmapped_values.join(', '),
        values: attr.unmapped_values,
        isCustom: attr.is_custom === true,
      });
    });
    (rawProduct.unmapped_attributes || []).forEach((attrName) => {
      if (!attrs.find((a) => a.name === attrName)) {
        attrs.push({ name: attrName, value: '-', values: [] });
      }
    });
    return attrs;
  };

  // Render metadata field (read-only, always imported style, no edit mode)
  const renderMetadataField = (label: string, value: string) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">{label}</span>
      <span className="tw-text-xs tw-font-semibold tw-text-ink">
        {value || '-'}
      </span>
    </div>
  );

  // Render chip field (label + individual grey chips for each value)
  const renderChipField = (label: string, values: string[]) => (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">{label}</span>
      <div className="tw-flex tw-flex-wrap tw-gap-1">
        {values.length > 0 ? values.map((val, i) => (
          <span
            key={i}
            className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-[3px] tw-min-h-[24px] tw-border-[0.5px] tw-border-solid tw-bg-tone-neutral-tint tw-border-edge-strong tw-text-ink tw-text-xs tw-font-semibold"
          >
            {val}
          </span>
        )) : (
          <span className="tw-text-xs tw-font-semibold tw-text-ink">-</span>
        )}
      </div>
    </div>
  );

  // Render grouped attribute chips (label + single chip per attribute with combined text)
  const renderGroupedAttributeChips = (
    label: string,
    groups: Array<{ attribute: string; unmapped_values: string[] }>
  ) => (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">{label}</span>
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {groups.map((group, groupIndex) => (
          <span
            key={groupIndex}
            className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-[3px] tw-min-h-[24px] tw-border-[0.5px] tw-border-solid tw-bg-tone-neutral-tint tw-border-edge-strong tw-text-ink tw-text-xs"
          >
            <span className="tw-font-normal">Attribute:&nbsp;</span>
            <span className="tw-font-semibold">{group.attribute}</span>
            <span className="tw-font-normal">,&nbsp;&nbsp;Attribute Value -&nbsp;</span>
            <span className="tw-font-semibold">{group.unmapped_values.join(', ')}</span>
          </span>
        ))}
      </div>
    </div>
  );

  // Render expanded content using ExpandedRowContent + footer
  const renderExpandedContent = (row: MissingMappingRowState) => (
    <>
      <ExpandedRowContent
        importedAttributes={buildImportedAttributes(row.rawProduct)}
        catalogAttributes={[]}
        isEditMode={false}
        singleSection={true}
        productName={row.product.name}
        // Match the outer colgroup. Missing Mapping has NO checkbox col,
        // so the # column is col 1 — pass hasCheckboxCol=false to skip
        // the leading spacer td.
        colWidths={['4%', '23%', '21%', '20%', '24%', '8%']}
        hasCheckboxCol={false}
      />
      {/* Footer — preview only, matches SKU matched imported-side read-only style */}
      <tr className="tw-bg-surface tw-border-t tw-border-edge">
        <td colSpan={6} className="tw-px-3 tw-py-2">
          {/* Row 1: Description */}
          <div className="tw-mb-2">
            {renderMetadataField('Description', row.product.description || '')}
          </div>
          {/* Row 2: SKU | Price | UPC | Model Number */}
          <div className="tw-flex tw-items-center tw-gap-8 tw-mb-2">
            {renderMetadataField('SKU', row.product.sku || '')}
            {renderMetadataField('Price', row.product.price || '')}
            {renderMetadataField('UPC', row.product.upc || '')}
            {renderMetadataField('Model Number', row.product.modelNumber || '')}
          </div>
          {/* Row 3: Status | Parent ID | Version | Real SKU */}
          <div className="tw-flex tw-items-center tw-gap-8 tw-mb-2">
            {renderMetadataField('PLS', row.product.status || '')}
            {renderMetadataField('Parent ID', row.product.parentId || '')}
            {renderMetadataField('Version', row.product.version || '')}
            {renderMetadataField('Real SKU', row.product.realSku || '')}
          </div>
          {/* Row 4: Unmapped Category | Unmapped Attribute (chips) | Unmapped Attribute Value (chips) */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            {!row.product.category && renderMetadataField('Unmapped Category', row.product.reportingCategory || '')}
            {row.product.unmappedAttributes.length > 0 && renderChipField('Unmapped Attribute', row.product.unmappedAttributes)}
            {(row.rawProduct.attributes_with_unmapped_values || []).length > 0 &&
              renderGroupedAttributeChips('Unmapped Attribute Value', row.rawProduct.attributes_with_unmapped_values)}
          </div>
        </td>
      </tr>
    </>
  );

  /*
    ONE close routine for every route out of the drawer — backdrop, the X, and (new with
    the DS SidePanel) Escape. Byte-for-byte what the hand-rolled backdrop and X each ran:
    close, refetch the unmapped list (mappings made in the drawer change it), tell the
    parent to refresh its counts. Business logic unchanged; only the shell moved.
  */
  const handleDrawerClose = () => {
    setIsMappingDrawerOpen(false);
    fetchProducts();
    onRefresh?.();
  };

  return (
    <div className="tw-space-y-4">
      {/* Custom Action Bar - Right aligned: Search + Mapping */}
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-px-3 tw-py-1">
        {/* DS Search (2026-09-09): the magnifier, the field chrome (hover whisper, the
            2px focus ring — the old input had only a border-colour swap, 1.77:1 as an
            indicator), a clear button once there is text, `role="searchbox"`. The testid
            lands on the <input>, exactly where the old one sat. */}
        <Search
          data-testid="missingmappingview-missingmappingview-input-1"
          value={searchInput}
          onChange={(value) => setSearchInput(value)}
          /* § 288 — `.trim()` on the left because that is what the debounce commits:
             comparing the raw value would leave the spinner running forever on a
             trailing space. `isLoading` then covers the fetch itself (it also covers
             the cold load and page changes, which read the same way — the list is
             being fetched). */
          loading={isLoading || searchInput.trim() !== searchTerm}
          loadingLabel="Searching products"
          placeholder="Search SKU, product, unmapped attribute or value"
          aria-label="Search missing mapping products"
          className="tw-w-80"
        />
        {/* No text colour here: `primary` owns its ink (`ink-inverse` on the fill). A
            `className="tw-text-ink"` added in the 2026-09-05 dark-mode pass lost on
            stylesheet order until Button started merging classes (DS § 330) — then it won,
            and the label went navy on navy. */}
        <Button data-testid="missing-mapping-open-drawer-btn" variant="primary" onClick={() => setIsMappingDrawerOpen(true)}>
          Mapping
        </Button>
      </div>

      {/*
        Cold load only. This view differs from the other six: its `isLoading` gates the
        ENTIRE table container, and the pagination strip lives INSIDE that container —
        so dropping `!isLoading` from the strip's own guard did nothing here, and every
        page click still unmounted table, footer and all. The table now survives a
        refetch and dims, same as the other six.
      */}
      {isLoading && unmappedRows.length === 0 && (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
          {/* DS Spinner (2026-09-09) — the § 220 arc ring, announced as a status; the
              hand-rolled border ring beside it was the module's last private spinner. */}
          <Spinner size="md" label="Loading..." showLabel data-testid="missing-mapping-loading" />
        </div>
      )}

      {/* Single Unified Table */}
      {(!isLoading || unmappedRows.length > 0) && (
        <div className="tw-relative tw-border tw-border-edge tw-rounded tw-flex tw-flex-col" style={{ maxHeight: 'calc(100vh - 280px)', minHeight: '400px' }}>
          {/*
            The "Refreshing" chip Guide/ISR shows on the same gesture. These
            tables are hand-rolled rather than the DS `Table`, so they got the
            dim without the chip — the rows faded and nothing said why. Shares
            `Table`'s implementation rather than restating it.

            `rows.length > 0` is the whole condition: a COLD load has nothing to
            dim and shows its spinner row instead, and drawing both would put
            "Refreshing" on top of "Loading...".
          */}
          <RefreshOverlay
            active={isLoading && unmappedRows.length > 0}
            data-testid="catalog-table-refreshing-missing-mapping"
          />
          {/* Header — does not scroll. flex-shrink-0 pins it at natural height;
              overflow:hidden creates a scroll container so
              scrollbar-gutter:stable actually reserves right-side space,
              keeping column edges aligned with the body table below. */}
          <div className="tw-flex-shrink-0" style={{ overflow: 'hidden', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse">
            {/* Column Groups for Width Definitions */}
            {/* # | Product | Category | Unmapped Attribute | Unmapped Attribute Value | Actions+Expand */}
            <colgroup>
              <col style={{ width: '4%' }} />
              <col style={{ width: '23%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '8%' }} />
            </colgroup>

            {/* Table Header - Two Rows */}
            <thead className="tw-bg-surface">
              {/* Section Header Row */}
              <tr className="tw-bg-surface">
                <th
                  colSpan={6}
                  className="tw-text-center tw-px-4 tw-py-3 tw-border-b tw-border-edge tw-bg-surface"
                >
                  <Typography variant="sub-title-label" className="tw-text-ink">
                    Imported Products
                  </Typography>
                </th>
              </tr>

              {/* Column Headers Row */}
              <tr className="tw-border-b tw-border-edge tw-bg-surface">
                {/* # column */}
                <th className="tw-px-2 tw-py-2.5 tw-h-[44px] tw-bg-tone-neutral-tint">
                  <div className="tw-flex tw-items-center tw-justify-center tw-text-ink-secondary">
                    <Hashtag />
                  </div>
                </th>
                {/* Product column */}
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint">
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Product
                    </Typography>
                  </span>
                </th>
                {/* Category column */}
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint">
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Category
                    </Typography>
                  </span>
                </th>
                {/* Unmapped Attribute column */}
                <th className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-whitespace-nowrap">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Unmapped Attribute
                    </Typography>
                  </span>
                </th>
                {/* Unmapped Attribute Value column */}
                <th className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-whitespace-nowrap">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Unmapped Attribute Value
                    </Typography>
                  </span>
                </th>
                {/* Actions + Expand column — no header label; centered to
                    match the body row's centered expand chevron. */}
                <th className="tw-px-2 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                </th>
              </tr>
            </thead>
          </table>
          </div>

          {/* Body — independent scroll container with the SAME colgroup as the
              header. scrollbar-gutter:stable on both sides ensures columns
              stay aligned regardless of whether the vertical scrollbar
              is visible. */}
          <div className="tw-flex-1" style={{ overflow: 'auto', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            <colgroup>
              <col style={{ width: '4%' }} />
              <col style={{ width: '23%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '8%' }} />
            </colgroup>

            {/* Table Body */}
            <tbody
              /* Dims through a refetch rather than unmounting — see the cold-load
                 comment above. `pointer-events-none` stops a click landing on a stale
                 row while the next page is in flight. */
              aria-busy={isLoading || undefined}
              className={`tw-transition-opacity ${isLoading ? 'tw-opacity-40 tw-pointer-events-none' : ''}`}
            >
              {unmappedRows.map((row, index) => {
                const isEvenRow = index % 2 === 1;
                const cellBgClass = `${isEvenRow ? 'tw-bg-surface-sunken' : 'tw-bg-surface'} group-hover:tw-bg-tone-neutral-tint`;

                return (
                  <React.Fragment key={row.product.id}>
                    {/* Main Row */}
                    <tr
                      className="tw-group tw-border-b tw-border-edge hover:tw-bg-tone-neutral-tint tw-h-[40px]"
                    >
                      {/* Row number */}
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <div className="tw-flex tw-items-center tw-justify-center">
                          <Typography variant="title-header" className="tw-text-ink">
                            {(pagination.page - 1) * pagination.perPage + index + 1}
                          </Typography>
                        </div>
                      </td>
                      {/* Product name */}
                      <td className={`tw-px-3 tw-py-2 ${cellBgClass}`} title={row.product.name}>
                        <Typography variant="title-header" className="tw-text-ink tw-truncate">
                          {row.product.name}
                        </Typography>
                      </td>
                      {/* Category */}
                      <td className={`tw-px-3 tw-py-2 ${cellBgClass}`} title={row.product.category || row.product.reportingCategory}>
                        <Typography variant="title-header" className="tw-text-ink">
                          {(row.product.category || row.product.reportingCategory || '').length > 18
                            ? `${(row.product.category || row.product.reportingCategory || '').slice(0, 18)}…`
                            : (row.product.category || row.product.reportingCategory)}
                        </Typography>
                      </td>
                      {/* Unmapped Attribute badge - Grey (Figma: grey-200 bg, grey-500 border) */}
                      <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-h-[28px] tw-px-2 tw-py-0.5 tw-rounded-[3px] tw-bg-tone-neutral-tint tw-border tw-border-edge-strong">
                          <Typography variant="title-header" className="tw-text-ink-secondary">
                            {row.product.unmappedAttributeCount}
                          </Typography>
                        </span>
                      </td>
                      {/* Unmapped Attribute Value badge - Grey (Figma: grey-200 bg, grey-500 border) */}
                      <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-h-[28px] tw-px-2 tw-py-0.5 tw-rounded-[3px] tw-bg-tone-neutral-tint tw-border tw-border-edge-strong">
                          <Typography variant="title-header" className="tw-text-ink-secondary">
                            {row.product.unmappedAttributeValueCount}
                          </Typography>
                        </span>
                      </td>
                      {/* Actions + Expand — centered per latest UX call */}
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                          <button data-testid={`missingmappingview-missingmappingview-button-2-${index}`}
                            onClick={() => handleRowExpand(index)}
                            className="tw-p-1 tw-rounded hover:tw-bg-surface-hover-strong tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
                          >
                            {row.isExpanded ? <ArrowDown /> : <ArrowRight />}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Row Content */}
                    {row.isExpanded && renderExpandedContent(row)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Empty State */}
          {unmappedRows.length === 0 && (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                No unmapped attributes found
              </Typography>
            </div>
          )}
          </div>

          {/*
             The guard no longer includes `!isLoading`. It used to, and the strip therefore
             UNMOUNTED on every page click: the button vanished from under the pointer,
             keyboard focus fell to <body>, and the row it occupied collapsed so the page
             jumped. It stays mounted and takes `disabled` instead — which is what Guide/ISR's
             equivalent does.
          
             What remains here is `total > perPage`, and it stays at the CALL SITE: whether a
             table needs a pager at all is the view's question, not the bar's.
          */}
          {pagination.total > pagination.perPage && (
            <TablePaginationBar
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              disabled={isLoading}
              data-testid="catalog-pagination-missing-mapping"
            />
          )}
        </div>
      )}

      {/*
        Mapping Drawer — the DS SidePanel (2026-09-09). The hand-rolled shell it replaces
        was a black-30% backdrop and a 75vw fixed panel with a stock shadow: no Escape, no
        focus trap or restore, no page-scroll lock, no motion, and a scrim that was wrong
        in dark. The SidePanel brings all of that (§ 53, § 218) on the theming seam.

        `tabs={[]}` is a bare drawer. The header band holds ONLY the close X (via
        `headerActions`, replacing the default Cancel/Save pair) because `MappingTabView`
        draws its own "Mapping" h1 — `mapping-title`, a contract in okf/mapping § 4 that
        the Mapping module's own page relies on — so a panel title above it would read
        twice; `ariaLabel` still names the dialog. `xl` is the widest DS rung (66vw; the
        old panel was 75vw). `data-testid="missing-mapping-drawer"` templates the backdrop
        to the `missing-mapping-drawer-backdrop` every spec already uses; the X keeps its
        auto-generated id. Every close route runs `handleDrawerClose`.

        Content — MappingTabView requires MappingContextProvider. Its Category tab
        (CategoryMappingTable/CreateCategoryModal) also reads ArsContext directly, and this
        route isn't one of the 7 wrapped in RECOMMENDER_PROVIDERS in App.tsx, so that has to
        be supplied locally too — see okf/mapping/e2e.md § 7. Context crosses the portal;
        only the DOM moves to <body>.
      */}
      <SidePanel
        isOpen={isMappingDrawerOpen}
        onClose={handleDrawerClose}
        title=""
        ariaLabel="Mapping"
        tabs={[]}
        width="xl"
        data-testid="missing-mapping-drawer"
        headerActions={
          <IconButton
            aria-label="Close"
            data-testid="missingmappingview-missingmappingview-button-3"
            variant="subtle"
            size="sm"
            onClick={handleDrawerClose}
          >
            <Cross size={20} aria-hidden="true" />
          </IconButton>
        }
      >
        <ArsContextProvider>
          <MappingContextProvider>
            <MappingTabView />
          </MappingContextProvider>
        </ArsContextProvider>
      </SidePanel>
    </div>
  );
};

export default MissingMappingView;
